import { ManagedServiceId } from '@axinom/mosaic-id-utils';
import {
  DateTimeTextField,
  Details,
  Divider,
  EmptyStation,
  MessageBar,
  ReadOnlyField,
  type StationMessage,
  TagsField,
  TextButton,
} from '@axinom/mosaic-ui';
import clsx from 'clsx';
import { config } from 'config';
import { Field, useFormikContext } from 'formik';
import type { BreadcrumbResolver } from 'models/breadcrumb';
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import * as Yup from 'yup';
import { ensureError } from '../../../../common/errors/ensure-error';
import {
  AzureStorageContainerSasTokenParamsDocument,
  type AzureStorageContainerSasTokenParamsPayload,
  type AzureStorageContainerSasTokenParamsQuery,
  GenerateAzureStorageContainerSasTokenDocument,
  type GenerateAzureStorageContainerSasTokenMutation,
  type GenerateAzureStorageContainerSasTokenMutationPayload,
} from '../../../../generated/hosting-service';
import { getManagedServiceAvailabilityMessage } from '../../../../util/managedServiceUtil';
import { environmentApolloClient } from '../../../environmentApolloClient';
import classes from './ContainerSasToken.module.scss';
import {
  CopySasToken,
  TokenCannotBeGenerated,
  UsedParametersToGenerate,
} from './container-sas-token-messages';

type SasParamsData = Pick<
  AzureStorageContainerSasTokenParamsPayload,
  'allowedIPs' | 'allowedProtocols' | 'permissions' | 'validFrom' | 'validUntil'
>;

type SasTokenValue = Pick<
  GenerateAzureStorageContainerSasTokenMutationPayload,
  'sasTokenUri' | 'sasTokenParams'
>;

export const ContainerSasToken: React.FC = () => {
  const { envId, storageAccountName, containerName } = useParams<{
    envId: string;
    action: string;
    storageAccountName: string;
    storageType: string;
    containerName: string;
  }>();

  const [sasParams, setSasParams] = useState<SasParamsData>();
  const [sasCredentials, setSasCredentials] = useState<SasTokenValue>();

  const [loading, setLoading] = useState<boolean>(false);
  const [message, setMessage] = useState<StationMessage>();
  const [serviceStatusMessage, setServiceStatusMessage] = useState<
    string | null
  >(null);
  const [error, setError] = useState<string>();
  const [isFormValid, setIsFormValid] = useState<boolean>();

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);

        // Check the status of hosting-service
        const statusMessage = await getManagedServiceAvailabilityMessage(
          ManagedServiceId.HOSTING_SERVICE,
          envId,
        );

        if (statusMessage !== null) {
          setServiceStatusMessage(statusMessage);
        } else {
          const envApolloClient = environmentApolloClient(
            envId,
            new URL(`/graphql`, config.hostingServiceAdminBaseUrl).toString(),
          );

          const sasParamsResult =
            await envApolloClient?.query<AzureStorageContainerSasTokenParamsQuery>(
              {
                query: AzureStorageContainerSasTokenParamsDocument,
                variables: {
                  input: {
                    containerName,
                  },
                },
                fetchPolicy: 'network-only',
              },
            );

          if (!sasParamsResult.data.azureStorageContainerSASTokenParams) {
            throw new Error(
              'Parameters loading failed. Token can not be generated.',
            );
          }

          if (sasParamsResult.data.azureStorageContainerSASTokenParams) {
            setSasParams(
              sasParamsResult.data.azureStorageContainerSASTokenParams,
            );
            setMessage(UsedParametersToGenerate);
          }
        }
      } catch (e) {
        const error = ensureError(e);
        setError(error.message);
      } finally {
        setLoading(false);
      }
    })();
  }, [envId, containerName]);

  const regenerateConnectionDetails = async (
    permissions: SasParamsData['permissions'],
    validFrom: string,
    validUntil: string,
  ): Promise<void> => {
    const envApolloClient = environmentApolloClient(
      envId,
      new URL(`/graphql`, config.hostingServiceAdminBaseUrl).toString(),
    );

    const sasTokenResult =
      await envApolloClient?.mutate<GenerateAzureStorageContainerSasTokenMutation>(
        {
          mutation: GenerateAzureStorageContainerSasTokenDocument,
          variables: {
            input: {
              storageAccountName,
              containerName,
              permissions,
              validFrom,
              validUntil,
            },
          },
        },
      );

    const sasToken =
      sasTokenResult?.data?.generateAzureStorageContainerSASToken;

    if (!sasToken) {
      setMessage(TokenCannotBeGenerated);
      return;
    }

    setSasParams((currentParams) => ({
      ...currentParams,
      permissions,
      validFrom,
      validUntil,
    }));
    setSasCredentials(sasToken);
    setMessage(CopySasToken);
  };

  return (
    <>
      {serviceStatusMessage ? (
        <EmptyStation title="Container SAS Token">
          <MessageBar type="warning" title={serviceStatusMessage}></MessageBar>
        </EmptyStation>
      ) : (
        <Details
          defaultTitle="Container SAS Token"
          initialData={{
            data: {
              ...sasParams,
              ...sasCredentials,
            },
            loading,
            error,
          }}
          validationSchema={Yup.object({
            permissions: Yup.array().min(1).required().label('Permissions'),
            validFrom: Yup.date()
              .typeError('Invalid date')
              .required()
              .label('Start Date'),
            validUntil: Yup.date()
              .typeError('Invalid date')
              .required()
              .min(Yup.ref('validFrom'), 'End Date cannot be before Start Date')
              .label('End Date'),
          })}
          saveData={() => Promise.resolve()}
          stationMessage={message}
          actions={[
            {
              label: 'Generate SAS Token',
              confirmationMode: 'Advanced',
              confirmationConfig: {
                body: 'Confirm to generate a SAS token with the provided parameters',
              },
              onActionSelected: async ({ values }) => {
                return regenerateConnectionDetails(
                  values.permissions,
                  values.validFrom ?? '',
                  values.validUntil ?? '',
                );
              },
              isDisabled: !isFormValid,
            },
          ]}
          showSaveHeaderAction={false}
        >
          {
            <Form
              params={sasParams}
              tokenDetails={sasCredentials}
              onIsValidChange={(isValid) => setIsFormValid(isValid)}
            />
          }
        </Details>
      )}
    </>
  );
};

const Form: React.FC<{
  params: SasParamsData | undefined;
  tokenDetails: SasTokenValue | undefined;
  onIsValidChange: (isValid: boolean) => void;
}> = ({ params, tokenDetails, onIsValidChange }) => {
  const { isValid } = useFormikContext();

  useEffect(() => {
    onIsValidChange(isValid);
  }, [isValid, onIsValidChange]);

  return (
    <>
      <Field
        name="permissions"
        label="Permissions"
        as={TagsField}
        value={params?.permissions ?? []}
        tagsOptions={['list', 'read', 'write', 'delete', 'add', 'create']}
      />
      <Field name="validFrom" label="Start Date" as={DateTimeTextField} />
      <Field name="validUntil" label="End Date" as={DateTimeTextField} />
      <Field name="allowedIPs" label="IP Range" as={ReadOnlyField} />
      <Field
        name="allowedProtocols"
        label="Allowed Protocols"
        as={ReadOnlyField}
      />

      {tokenDetails ? <Divider /> : null}

      {tokenDetails?.sasTokenUri && (
        <PlainTextReadonlyField
          name="sasTokenUri"
          label="SAS URI"
          value={tokenDetails.sasTokenUri}
        />
      )}
      {tokenDetails?.sasTokenParams && (
        <PlainTextReadonlyField
          name="sasTokenParams"
          label="SAS Token"
          value={tokenDetails.sasTokenParams}
        />
      )}
    </>
  );
};

const PlainTextReadonlyField: React.FC<{
  name: string;
  label: string;
  value?: string | null;
}> = ({ name, label, value }) => {
  const onCopyClickedHandler = (copyValue?: string | null): void => {
    navigator.clipboard.writeText(copyValue ?? '');
  };

  return (
    <div className={clsx(classes.password)}>
      <Field
        name={name}
        label={label}
        as={ReadOnlyField}
        value={value}
        className={clsx(classes.content_wrap)}
      />
      <>
        <TextButton
          className={classes.button}
          text="Copy"
          onButtonClicked={() => onCopyClickedHandler(value)}
        />
      </>
    </div>
  );
};

export const ContainerSasTokenCrumb: BreadcrumbResolver = () => 'SAS Token';
